import { useState, ChangeEvent } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { copyToClipboard } from "@/lib/utils";
import { Copy, Trash2, ArrowLeftRight } from "lucide-react";
import { ToolPageLayout } from "@/components/ToolPageLayout";

export function Base64Tool() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [mode, setMode] = useState<"encode" | "decode">("encode");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const processText = (text: string, currentMode: "encode" | "decode") => {
    if (!text) {
      setOutput("");
      setError(null);
      return;
    }
    try {
      if (currentMode === "encode") {
        // Handle utf-8 encoding for Base64 correctly
        const bytes = new TextEncoder().encode(text);
        const binString = Array.from(bytes, (byte) =>
          String.fromCodePoint(byte),
        ).join("");
        setOutput(btoa(binString));
      } else {
        const binString = atob(text);
        const bytes = Uint8Array.from(binString, (m) => m.codePointAt(0)!);
        setOutput(new TextDecoder().decode(bytes));
      }
      setError(null);
    } catch (e) {
      setError("Invalid input for decoding.");
      setOutput("");
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);
    processText(val, mode);
  };

  const toggleMode = () => {
    const newMode = mode === "encode" ? "decode" : "encode";
    setMode(newMode);
    setInput(output); // swap input and output
    processText(output, newMode);
  };

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Base64 Encoder & Decoder Online"
      description="Encode and decode Base64 strings instantly with our free online tool."
      h1="Base64 Encoder & Decoder"
      category="Encoding Tools"
      categoryPath="/encoding-tools"
      howToUse={[
        "Select 'Encode' or 'Decode' mode.",
        "Paste your text or Base64 string into the input area.",
        "The result will be displayed instantly in the output area."
      ]}
      faq={[
        { question: "What is Base64?", answer: "Base64 is a way to represent binary data as ASCII string text." }
      ]}
    >
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button onClick={toggleMode} variant="outline" className="gap-2 w-full md:w-auto">
            <ArrowLeftRight className="h-4 w-4" />
            Switch to {mode === "encode" ? "Decode" : "Encode"}
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              {mode === "encode" ? "Plain Text Input" : "Base64 Input"}
            </label>
            <Textarea
              placeholder={mode === "encode" ? "Hello World" : "SGVsbG8gV29ybGQ="}
              className="min-h-[250px]"
              value={input}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              {mode === "encode" ? "Base64 Output" : "Plain Text Output"}
            </label>
            <Textarea
              className="min-h-[250px] bg-slate-50"
              value={error ? error : output}
              readOnly
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleCopy} disabled={!!error || !output} variant="outline" className="gap-2">
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy Output"}
          </Button>
          <Button onClick={() => { setInput(""); setOutput(""); setError(null); }} variant="ghost" className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
            Clear
          </Button>
        </div>
      </div>
    </ToolPageLayout>
  );
}
